import { format, parseISO } from 'date-fns';
import { i18n } from '@/shared/i18n';

const EM_DASH = '—';

/** Formats a date string as "Mon DD, YYYY" (e.g. "Mar 20, 2026"), in `locale` when given. */
export function formatShortDate(date: string | undefined | null, locale = 'en-US'): string {
  if (!date) return '-';

  // parseISO treats a date-only "YYYY-MM-DD" as local midnight (not UTC), so
  // a Postgres `date` column round-trips without shifting a day in west-of-UTC
  // zones. Full timestamps with offsets are parsed correctly too.
  const parsed = parseISO(date);
  if (isNaN(parsed.getTime())) return '-';

  return parsed.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * The "last modified" stamp the Knowledge Hub list tables share.
 *
 * Guarded rather than formatted straight: `date-fns` throws on an unparseable value instead
 * of degrading, and these run inside a `.map()` over rows, so one bad timestamp would take
 * the whole table down rather than blanking its own cell.
 */
export function formatTableTimestamp(value: string | undefined | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return EM_DASH;
  // eslint-disable-next-line i18next/no-literal-string -- date-fns pattern, machine input rather than translator-facing copy
  return format(parsed, 'MMM d, h:mmaaa');
}

/** Formats bytes into a human-readable string (KB, MB, GB). */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return i18n.t('common:utils.format.bytes', { size: bytes });
  if (bytes < 1024 * 1024)
    return i18n.t('common:utils.format.kilobytes', { size: (bytes / 1024).toFixed(1) });
  if (bytes < 1024 * 1024 * 1024)
    return i18n.t('common:utils.format.megabytes', { size: (bytes / (1024 * 1024)).toFixed(1) });
  return i18n.t('common:utils.format.gigabytes', {
    size: (bytes / (1024 * 1024 * 1024)).toFixed(1),
  });
}

/** Extracts the uppercase file extension from a filename (e.g. "photo.jpg" → "JPG"). */
export function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop()!.toUpperCase() : '';
}

// Covers the MIMEs accepted by chat/project uploads (libs/file-size CHAT_UPLOAD_MIME_TYPES).
// Lossy MIMEs (DOC vs DOCX share a type) collapse to the modern extension.
const MIME_TO_EXTENSION: Record<string, string> = {
  'image/jpeg': 'JPG',
  'image/jpg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WEBP',
  'image/gif': 'GIF',
  'image/svg+xml': 'SVG',
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'video/webm': 'WEBM',
  'video/3gpp': '3GP',
  'video/ogg': 'OGV',
  'video/mpeg': 'MPEG',
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-excel': 'XLS',
  'application/json': 'JSON',
  'application/zip': 'ZIP',
  'text/markdown': 'MD',
  'text/x-markdown': 'MD',
  'text/plain': 'TXT',
  'text/csv': 'CSV',
  'text/html': 'HTML',
};

/**
 * Derives an uppercase extension label from a MIME type, falling back to the
 * filename when the MIME isn't in the curated map. Prefer this over
 * `getFileExtension` when both are available — renaming a file shouldn't
 * change its extension badge.
 */
export function getExtensionFromFileType(fileType: string, fileName?: string): string {
  const mapped = MIME_TO_EXTENSION[fileType.toLowerCase()];
  if (mapped) return mapped;
  return fileName ? getFileExtension(fileName) : '';
}

/**
 * Formats a whole-unit amount as currency (e.g. 12000, "EUR" → "€12,000").
 * Fractional units are dropped — the money this renders is media budget and
 * spend, where cents are noise on a scannable row.
 */
export function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Converts a snake_case string to Title Case (e.g. "one_off" → "One Off"). */
export function formatSnakeCase(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
